import { FC, useState, useEffect } from 'react';
import { Box, Typography } from '@mui/material';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import dayjs from 'dayjs';
import MainLayout from 'src/pages/private';
import { useFormik } from 'formik';
import { orderPaymentInitialValues } from 'src/utils/initialValues.utils';
import { orderPaymentDynamicValidation } from 'src/utils/validationScheme.utils';
import StyledSnackbar from 'src/components/StyledComponents/StyledSnackBar';
import {
  apiUrls,
  routeURL,
  pageTitles,
  orderPaymentDialogTitles,
  orderPaymentDialogSubtitles,
  DATE_FORMATS,
} from 'src/utils/properties.utils';
import { axiosTokenInstance } from 'src/utils/axiosInstance.utils';
import OrderPaymentGrid from 'src/components/Grids/OrderPaymentGrid';
import AddOrderPaymentDialog from 'src/components/DialogBox/OrderPaymentDialog/AddOrderPaymentDialog';
import TextField from 'src/components/InputFields/TextField';
import TextArea from 'src/components/InputFields/TextArea';
import PaymentModePicker from 'src/components/InputFields/PaymentModePicker';
import CreatedDatePicker from 'src/components/InputFields/DatePicker';
import { getApiErrorMessage } from 'src/utils/errorHandler';
import { StyledDatePickerErrorText } from 'src/components/StyledComponents/StyledUnitPicker';
import ErrorIcon from 'src/components/InputFields/ErrorIcon';
import { PaymentMode } from 'src/models/enums/PaymentMode.enum';
import {
  OrderDetailsResponse,
  OrderPaymentPayload,
} from 'src/models/OrderPayment.interface';
import {
  PageHeaderBox,
  TitleSection,
  BackButton,
  BackIcon,
  PageTitle,
  PrimaryActionButton,
  ButtonIcon,
  OrderPageTitle,
} from 'src/components/StyledComponents/OrderPayments.styles';
import FormikSelectField from 'src/components/InputFields/FormikSelectField';
import { useGetBasicBanks } from 'src/services/bank.service';
import {
  formatNumberWithCommas,
  handleNumberFieldChange,
} from 'src/utils/format.utils';

interface LocationState {
  orderNumber?: string;
}

const OrderPayments: FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { orderId } = useParams<{ orderId: string }>();
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [paymentAdded, setPaymentAdded] = useState(false);
  const [grandTotal, setGrandTotal] = useState<number | null>(null);
  const [totalPaidAmount, setTotalPaidAmount] = useState<number | null>(null);

  // Get orderNumber from navigation state if available
  const locationState = location.state as LocationState | null;
  const [orderNumber, setOrderNumber] = useState<string>(
    locationState?.orderNumber || ''
  );

  const { showSnackbar } = StyledSnackbar();

  const { basicBanks, fetchBasicBanks } = useGetBasicBanks();

  const accountOptions = basicBanks.map((bank) => ({
    label: `${bank.bankName} - ${bank.accountNumber}`,
    value: bank.id.toString(),
  }));

  const remainingAmount =
    grandTotal !== null && totalPaidAmount !== null
      ? Math.max(grandTotal - totalPaidAmount, 0)
      : null;

  const fetchOrderDetails = async () => {
    if (!orderId) {
      return;
    }

    try {
      const response = await axiosTokenInstance.get<OrderDetailsResponse>(
        `${apiUrls.GET_ORDER_BY_ID}/${orderId}`
      );
      const orderDetails = response.data;

      setOrderNumber(
        orderDetails?.orderNumber || locationState?.orderNumber || ''
      );
      setGrandTotal(Number(orderDetails?.grandTotal) || 0);
      setTotalPaidAmount(Number(orderDetails?.totalPaidAmount) || 0);
    } catch (error) {
      console.error('Failed to fetch order details:', error);
    }
  };

  // Fetch banks only when payment mode requires bank account (CHEQUE or ONLINE)
  const handleFetchBanksIfNeeded = (paymentMode: PaymentMode | '') => {
    if (
      (paymentMode === PaymentMode.CHEQUE ||
        paymentMode === PaymentMode.ONLINE) &&
      basicBanks.length === 0
    ) {
      fetchBasicBanks();
    }
  };

  const handleClickOpen = async () => {
    await fetchOrderDetails();
    setOpen(true);
  };

  const handleClose = () => {
    formik.resetForm();
    setOpen(false);
  };

  const handleBack = () => {
    if (orderId) {
      navigate(`${routeURL.USER}${routeURL.ORDER_MANAGEMENT}?view=${orderId}`);
    } else {
      navigate(routeURL.USER + routeURL.ORDER_MANAGEMENT);
    }
  };

  const formik = useFormik({
    initialValues: orderPaymentInitialValues,
    validationSchema: orderPaymentDynamicValidation,
    validateOnChange: true,
    validateOnBlur: true,
    enableReinitialize: true,
    onSubmit: () => {
      // Submission handled in handleSavePayment
    },
  });

  const handleSavePayment = async () => {
    // Define all fields that should be touched based on payment mode
    const touchedFields: Record<string, boolean> = {
      paymentDate: true,
      amount: true,
      paymentMode: true,
    };

    // Add conditional fields based on payment mode
    if (formik.values.paymentMode === PaymentMode.CHEQUE) {
      touchedFields.chequeNumber = true;
      touchedFields.chequeDate = true;
      touchedFields.bankAccountId = true;
    } else if (formik.values.paymentMode === PaymentMode.ONLINE) {
      touchedFields.bankAccountId = true;
      touchedFields.transactionReference = true;
    }

    // Validate using the dynamic schema
    try {
      await orderPaymentDynamicValidation.validate(formik.values, {
        abortEarly: false,
      });
    } catch (validationError) {
      const yupError = validationError as {
        inner: Array<{ path: string; message: string }>;
      };
      const errors = yupError.inner.reduce(
        (
          acc: Record<string, string>,
          error: { path: string; message: string }
        ) => {
          acc[error.path] = error.message;
          return acc;
        },
        {}
      );
      // Set touched first, then errors
      await formik.setTouched(touchedFields, false);
      formik.setErrors(errors);
      return;
    }

    const enteredAmount = parseFloat(formik.values.amount);
    if (
      remainingAmount !== null &&
      remainingAmount > 0 &&
      enteredAmount > remainingAmount
    ) {
      await formik.setTouched({ ...touchedFields }, false);
      formik.setErrors({
        amount: `Amount exceeds payable amount`,
      });
      return;
    }

    setLoading(true);

    try {
      const payload: OrderPaymentPayload = {
        orderId: orderId || '',
        paymentDate: dayjs(formik.values.paymentDate).format(DATE_FORMATS.API),
        amount: parseFloat(formik.values.amount.replace(/,/g, '')),
        paymentMode: formik.values.paymentMode,
      };

      if (formik.values.notes) {
        payload.notes = formik.values.notes;
      }

      if (formik.values.paymentMode === PaymentMode.CHEQUE) {
        payload.chequeNumber = formik.values.chequeNumber;
        payload.chequeDate = dayjs(formik.values.chequeDate).format(
          DATE_FORMATS.API
        );
        payload.bankAccountId = formik.values.bankAccountId;
      } else if (formik.values.paymentMode === PaymentMode.ONLINE) {
        payload.bankAccountId = formik.values.bankAccountId;
        payload.paymentRefNo = formik.values.transactionReference;
      }

      const response = await axiosTokenInstance.post(
        apiUrls.ADD_ORDER_PAYMENT,
        payload
      );
      showSnackbar(response.data.message, 'success');
      setPaymentAdded((prev) => !prev);
      handleClose();
    } catch (error) {
      const errorMessage = await getApiErrorMessage(error);
      showSnackbar(errorMessage, 'error');
    } finally {
      setLoading(false);
    }
  };

  // Fetch banks when payment mode changes to CHEQUE or ONLINE
  useEffect(() => {
    handleFetchBanksIfNeeded(formik.values.paymentMode);
  }, [formik.values.paymentMode]);

  return (
    <MainLayout title="Order Management">
      {!orderId ? (
        <Typography>Invalid Order ID</Typography>
      ) : (
        <>
          <PageHeaderBox>
            <TitleSection>
              <BackButton onClick={handleBack}>
                <BackIcon component="i" className="bx bx-chevron-left" />
              </BackButton>
              <PageTitle>{pageTitles.ORDER_PAYMENTS}</PageTitle>{' '}
              <OrderPageTitle>#{orderNumber}</OrderPageTitle>
            </TitleSection>
            <PrimaryActionButton onClick={handleClickOpen}>
              <ButtonIcon component="i" className="bx bx-plus" />
              New Payment
            </PrimaryActionButton>
          </PageHeaderBox>
          <OrderPaymentGrid
            orderId={orderId}
            state={paymentAdded}
            banks={basicBanks}
            onBanksFetch={fetchBasicBanks}
            onPaymentModeChange={handleFetchBanksIfNeeded}
          />
          <AddOrderPaymentDialog
            open={open}
            handleClose={handleClose}
            title={orderPaymentDialogTitles.ADD}
            subtitle={orderPaymentDialogSubtitles.ADD}
            onSubmit={formik.handleSubmit}
            buttonDisabled={loading}
            buttonLabel="Save"
            onSaveClick={handleSavePayment}
            paymentModeSection={
              <>
                <Box mb={0}>
                  <PaymentModePicker
                    color="var(--subtext)"
                    width="48.5%"
                    id="paymentMode"
                    label="Payment Method"
                    formikProps={formik}
                    customMessage="Select payment method"
                    showLabel={false}
                  />
                </Box>
                {formik.values.paymentMode === PaymentMode.CHEQUE && (
                  <>
                    <Box
                      display={'flex'}
                      justifyContent={'space-between'}
                      gap={'16px'}
                      mb={0}
                      mt={-3}
                    >
                      <Box flex={1}>
                        <TextField
                          color="var(--subtext)"
                          width="100%"
                          id="chequeNumber"
                          label="Cheque Number"
                          formikProps={formik}
                        />
                      </Box>
                      <Box flex={1}>
                        <Box mb={3}>
                          <Typography
                            fontFamily="Outfit"
                            fontWeight="400"
                            fontSize="14px"
                            color="var(--subtext)"
                            mb={2}
                          >
                            Cheque Date
                          </Typography>
                          <CreatedDatePicker
                            fontSize="14px"
                            height="40px"
                            color="var(--subtext)"
                            width="100%"
                            name="chequeDate"
                            placeholder="Select date"
                            formikProps={formik}
                            showLabel={false}
                          />
                          {formik.touched.chequeDate &&
                            formik.errors.chequeDate && (
                              <StyledDatePickerErrorText>
                                <ErrorIcon />
                                {formik.errors.chequeDate}
                              </StyledDatePickerErrorText>
                            )}
                        </Box>
                      </Box>
                    </Box>
                    <Box
                      display={'flex'}
                      justifyContent={'space-between'}
                      mb={0}
                      gap={2}
                    >
                      <Box flex={1}>
                        <FormikSelectField
                          id="bankAccountId"
                          label="Bank Account"
                          formikProps={formik}
                          width="48.5%"
                          options={accountOptions}
                        />
                      </Box>
                    </Box>
                  </>
                )}
                {formik.values.paymentMode === PaymentMode.ONLINE && (
                  <Box
                    display={'flex'}
                    justifyContent={'space-between'}
                    mb={0}
                    mt={-3}
                  >
                    <Box flex={1}>
                      <FormikSelectField
                        id="bankAccountId"
                        label="Bank Account"
                        formikProps={formik}
                        width="100%"
                        options={accountOptions}
                      />
                    </Box>
                    <Box flex={1} display={'flex'} justifyContent={'flex-end'}>
                      <TextField
                        color="var(--subtext)"
                        width="100%"
                        id="transactionReference"
                        label="Payment Reference"
                        formikProps={formik}
                      />
                    </Box>
                  </Box>
                )}
              </>
            }
          >
            <Box
              display={'flex'}
              justifyContent={'space-between'}
              mb={0}
              gap={'16px'}
            >
              <Box flex={1} mb={0}>
                <TextField
                  color="var(--subtext)"
                  width="100%"
                  id="amount"
                  label="Amount"
                  formikProps={formik}
                  type="text"
                  value={formatNumberWithCommas(formik.values.amount)}
                  onChange={(e) => handleNumberFieldChange(e, formik, 'amount')}
                  placeholder={
                    remainingAmount !== null && remainingAmount > 0
                      ? `Remaining: ${remainingAmount.toLocaleString()}`
                      : ''
                  }
                />
              </Box>
              <Box
                flex={1}
                display={'flex'}
                flexDirection={'column'}
                justifyContent={'flex-end'}
              >
                <CreatedDatePicker
                  mb={3}
                  fontSize="14px"
                  label="Payment Date"
                  showLabel={true}
                  color="var(--subtext)"
                  width="100%"
                  height="40px"
                  name="paymentDate"
                  placeholder="Payment Date"
                  formikProps={formik}
                />
                {formik.touched.paymentDate && formik.errors.paymentDate && (
                  <StyledDatePickerErrorText top="2px">
                    <ErrorIcon />
                    {formik.errors.paymentDate}
                  </StyledDatePickerErrorText>
                )}
              </Box>
            </Box>
            <Box mb={0}>
              <TextArea
                id="notes"
                label="Notes (Optional)"
                formikProps={formik}
                placeholder="Add any notes about this payment..."
                height="80px"
                width="100%"
                showLabel={true}
                rows={2}
                overflowY="hidden"
              />
            </Box>
          </AddOrderPaymentDialog>
        </>
      )}
    </MainLayout>
  );
};

export default OrderPayments;
